import React, { useMemo, useState } from "react";
import { Handle, Position } from "reactflow";
import { Box, Chip, TextField, Typography } from "@mui/material";

const TYPE_COLOR = {
  Start: "default",
  Event: "default",
  End: "default",
  CreateLead: "primary",
  Tag: "secondary",
  ChannelMessage: "success",
  Condition: "warning",
  Delay: "info",
};

export default function FlowNode({ id, data, selected }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(data?.label || ""));

  const nodeType = String(data?.nodeType || "Action");
  const chipColor = TYPE_COLOR[nodeType] || "default";

  const borderColor = selected ? "primary.main" : "divider";
  const bg = selected ? "rgba(25,118,210,0.06)" : "background.paper";

  const supportsInput = nodeType !== "Start";
  const supportsOutput = nodeType !== "End";

  const title = useMemo(() => {
    const label = String(data?.label || id);
    if (label.length <= 42) return label;
    return `${label.slice(0, 42)}…`;
  }, [data?.label, id]);

  return (
    <Box
      sx={{
        minWidth: 190,
        maxWidth: 260,
        borderRadius: 2,
        border: "1px solid",
        borderColor,
        bgcolor: bg,
        boxShadow: selected ? 3 : 1,
        px: 1.25,
        py: 1,
      }}
      onDoubleClick={() => {
        setDraft(String(data?.label || ""));
        setEditing(true);
      }}
    >
      {supportsInput && <Handle type="target" position={Position.Left} style={{ width: 10, height: 10 }} />}
      {supportsOutput && <Handle type="source" position={Position.Right} style={{ width: 10, height: 10 }} />}

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Chip size="small" label={nodeType} color={chipColor} variant={chipColor === "default" ? "outlined" : "filled"} />
        <Typography variant="caption" color="text.secondary">
          {id}
        </Typography>
      </Box>

      <Box mt={0.75}>
        {editing ? (
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              setEditing(false);
              data?.onRename?.(id, draft);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
                setDraft(String(data?.label || ""));
              }
            }}
            autoFocus
            fullWidth
            size="small"
          />
        ) : (
          <Typography fontWeight={800} sx={{ wordBreak: "break-word" }}>
            {title || "—"}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

